import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  Tag,
  Tabs,
  Tooltip,
  Popover,
  Button,
  Row,
  notification,
  Modal,
  DatePicker,
  Space,
  Select,
  Input,
  Alert,
  Spin,
  Typography,
} from "antd";
import { useLoading } from "../../context/loadingContext";
import dayjs from "dayjs";
import GetAdminInvoices from "../../functions/admin/getAdminInvoices";
import ExportAdminInvoices from "../../functions/admin/exportAdminInvoices";
import { downloadAdminInvoiceExcel } from "../../functions/admin/buildAdminInvoiceExcel";
import {
  invoiceControlRow,
  invoicePaidAt,
  settleStateLabel,
} from "../../functions/admin/invoiceControlRow";
import {
  invoiceIncomeProductName,
  invoiceIsPack,
} from "../../functions/admin/invoiceIncomeType";
import GetMerchants from "../../functions/admin/getMerchants";
import GetPaymentMethods from "../../functions/reference/getPaymentMethods";
import GetProductTypes from "../../functions/reference/getProductType";
import axios from "axios";
import {
  CodeOutlined,
  LinkOutlined,
  RedoOutlined,
  FileExcelOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useAdmin } from "../../context/adminContext";
import moneyFormat from "../../functions/utils/moneyFormat";
import { invoiceSearchQueryString } from "../../functions/admin/invoiceSearchParams";
import {
  describeInvoiceExportFilters,
  INVOICE_SUMMARY_ROWS,
} from "../../functions/admin/describeInvoiceExportFilters";
import AdminInvoiceAdd from "../../components/modal/adminInvoiceAdd";
import { bg_button, bg_shadow } from "constant/css";
import TablePagination from "components/pagination/tablePagination";
import ExpenseTransactions from "./expenseTransactions";
import InvoiceEventLogTab from "./invoiceEventLogTab";
import { adminInvoiceTxRangeFromQuery } from "../../functions/admin/adminInvoiceDeepLink";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const InvoiceSummaryPreview = ({ title, summary, total, loading }) => (
  <div className="rounded-lg border border-gray-200 p-3 text-sm">
    <div className="mb-2 font-medium">{title}</div>
    {loading ? (
      <Spin size="small" />
    ) : (
      <>
        <div className="mb-2 text-gray-600">
          Мөр тоо: <Text strong>{total ?? "—"}</Text>
        </div>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {INVOICE_SUMMARY_ROWS.map((row) => (
            <div key={row.key} className="flex justify-between gap-2">
              <span className="text-gray-600">{row.label}</span>
              <span className="font-medium tabular-nums">
                {summary?.[row.key] == null || summary?.[row.key] === ""
                  ? "—"
                  : moneyFormat(summary[row.key])}
              </span>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

const moneyCell = (value) => (value == null ? "-" : moneyFormat(value));
const timeCell = (value) =>
  value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";
const percentCell = (value) =>
  value == null ? "-" : Number(value).toFixed(2);

const activeValue = (value) =>
  value != null && value !== "" ? [String(value)] : null;

const asNumber = (value) => {
  const text = String(value ?? "").replace(/[\s,]/g, "");
  return text && Number.isFinite(Number(text)) ? text : null;
};

const filterIcon = (filtered) => (
  <SearchOutlined style={{ color: filtered ? "#13a38b" : undefined }} />
);

const purposeText = (value) => {
  const text = String(value ?? "").trim();
  return text || null;
};

const uniquePurposeTexts = (values) => {
  const seen = [];
  (values || []).forEach((value) => {
    const text = purposeText(value);
    if (text && !seen.includes(text)) seen.push(text);
  });
  return seen;
};

const PurposeLines = ({ record }) => {
  const groups = [
    {
      key: "invoice",
      label: "Нэхэмжлэл",
      texts: uniquePurposeTexts([record.description]),
    },
    {
      key: "item",
      label: "Мөр",
      texts: uniquePurposeTexts(
        (record.invoiceItems || []).map((item) => item.description)
      ),
    },
    {
      key: "bank",
      label: "Банк",
      texts: uniquePurposeTexts(record.bankDescriptions),
    },
  ];
  return (
    <div className="invoice-purpose">
      {groups.map((group) => {
        const texts = group.texts.length ? group.texts : [null];
        return texts.map((text, index) => (
          <div className="invoice-purpose__line" key={`${group.key}-${index}`}>
            <span className="invoice-purpose__label">
              {index === 0 ? group.label : ""}
            </span>
            {text ? (
              <Tooltip title={text}>
                <span className="invoice-purpose__text">{text}</span>
              </Tooltip>
            ) : (
              <span className="invoice-purpose__text invoice-purpose__text--empty">
                —
              </span>
            )}
          </div>
        ));
      })}
    </div>
  );
};

const AdminInvoice = () => {
  const { showLoading, hideLoading } = useLoading();
  const { searchFields, setSearchFields } = useAdmin();
  const [searchParams] = useSearchParams();
  const [urlTxRangeLabel, setUrlTxRangeLabel] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [devMode, setDevMode] = useState(false);

  const [summary, setSummary] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [paginationData, setPaginationData] = useState({ pageSize: 20 });
  const [openModal, setOpenModal] = useState(false);
  const [excelModal, setExcelModal] = useState(false);
  const [excelRange, setExcelRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("day"),
  ]);
  const [excelExporting, setExcelExporting] = useState(false);
  const [excelPreviewLoading, setExcelPreviewLoading] = useState(false);
  const [excelPreview, setExcelPreview] = useState(null);
  const [controlTab, setControlTab] = useState("income");
  const [recipientDraft, setRecipientDraft] = useState(
    () => searchFields.recipient || ""
  );
  const [purposeDraft, setPurposeDraft] = useState(
    () => searchFields.purpose || ""
  );
  const [invoiceIdDraft, setInvoiceIdDraft] = useState(
    () => searchFields.id || ""
  );
  useEffect(() => {
    setRecipientDraft(searchFields.recipient || "");
  }, [searchFields.recipient]);
  useEffect(() => {
    setPurposeDraft(searchFields.purpose || "");
  }, [searchFields.purpose]);
  useEffect(() => {
    setInvoiceIdDraft(searchFields.id || "");
  }, [searchFields.id]);
  const dateReady = useRef(false);
  const todayRange = () => [dayjs().startOf("day"), dayjs().endOf("day")];
  useEffect(() => {
    return () => {
      setSearchFields((prev) => {
        if (
          !prev?.createdAt &&
          !prev?.recipient &&
          !prev?.productTypeId &&
          !prev?.purpose &&
          !prev?.id
        )
          return prev;
        const next = { ...prev };
        delete next.createdAt;
        delete next.recipient;
        delete next.productTypeId;
        delete next.purpose;
        delete next.id;
        return next;
      });
    };
  }, [setSearchFields]);
  useEffect(() => {
    const hasDate = searchFields?.createdAt?.[0] && searchFields?.createdAt?.[1];
    if (!dateReady.current) {
      dateReady.current = true;
      const fromUrl = adminInvoiceTxRangeFromQuery(
        searchParams.get("txFrom"),
        searchParams.get("txTo")
      );
      if (fromUrl) {
        setSearchFields((prev) => ({
          ...prev,
          createdAt: fromUrl.dayjsRange,
        }));
        setUrlTxRangeLabel(
          fromUrl.txFrom === fromUrl.txTo
            ? fromUrl.txFrom
            : `${fromUrl.txFrom} — ${fromUrl.txTo}`
        );
        return;
      }
      if (!hasDate) {
        setSearchFields((prev) => ({
          ...prev,
          createdAt: todayRange(),
        }));
        return;
      }
    }
    prepareQueryStringAndSearch();
  }, [searchFields, page, pageSize, searchParams]);
  const prepareQueryStringAndSearch = () => {
    const queryString = invoiceSearchQueryString(searchFields);
    fetchInvoices(
      `${queryString}${queryString ? "&" : ""}controlSummary=1`,
      page,
      pageSize
    );
  };
  const setFilter = (fieldName, value) => {
    setPage(1);
    setSearchFields((prev) => ({
      ...prev,
      [fieldName]: value || null,
    }));
  };
  const textFilter = (field, placeholder, normalize) => ({
    filteredValue: activeValue(searchFields[field]),
    filterIcon,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div
        className="invoice-control-filter"
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Input
          size="small"
          allowClear
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(event) =>
            setSelectedKeys(event.target.value ? [event.target.value] : [])
          }
          onPressEnter={(event) => {
            const raw = event.target.value;
            setFilter(
              field,
              normalize ? normalize(raw) : String(raw || "").trim()
            );
            confirm();
          }}
        />
        <Space style={{ marginTop: 8 }}>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              const raw = selectedKeys[0];
              setFilter(field, normalize ? normalize(raw) : String(raw || "").trim());
              confirm();
            }}
          >
            Хайх
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              setFilter(field, null);
              confirm();
            }}
          >
            Цэвэрлэх
          </Button>
        </Space>
      </div>
    ),
  });
  const selectFilter = (field, options) => ({
    filteredValue: activeValue(searchFields[field]),
    filterIcon,
    filterDropdown: ({ confirm }) => (
      <div
        className="invoice-control-filter"
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          placeholder="Сонгох"
          value={searchFields[field] || undefined}
          options={options}
          onChange={(value) => {
            setFilter(field, value);
            confirm();
          }}
        />
      </div>
    ),
  });
  const defaultColumns = [
    {
      title: "№",
      key: "no",
      width: 48,
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Код",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Нэхэмжлэлийн цаг",
      dataIndex: "createdAt",
      key: "invoiceCreatedAt",
      width: 150,
      render: (value) => timeCell(value),
    },
    {
      title: (
        <Tooltip title="Зөвхөн invoice.paidAt. Хоосон бол backfill хийгээгүй эсвэл SQL apply хийгээгүй.">
          <span>Төлөгдсөн цаг</span>
        </Tooltip>
      ),
      key: "paidAt",
      width: 150,
      render: (_, record) => timeCell(invoicePaidAt(record)),
    },
    {
      title: (
        <Tooltip title="Callback хадгалах зэрэг сүүлийн өөрчлөлт (updatedAt). Төлсөн цаг биш.">
          <span>Сүүлд шинэчлэгдсэн цаг</span>
        </Tooltip>
      ),
      dataIndex: "updatedAt",
      key: "invoiceUpdatedAt",
      width: 150,
      render: (value) => timeCell(value),
    },
    {
      title: "Төрөл",
      key: "incomeType",
      width: 80,
      align: "center",
      render: (_, record) => {
        const pack = invoiceIsPack(record);
        const productName = invoiceIncomeProductName(record);
        const tag = (
          <Tag color={pack ? "purple" : "default"}>
            {pack ? "Багц" : "Дан"}
          </Tag>
        );
        return productName ? <Tooltip title={productName}>{tag}</Tooltip> : tag;
      },
    },
    {
      title: (
        <Tooltip title="Нэхэмжлэл — нэхэмжлэлийн тайлбар. Мөр — нэхэмжлэлийн мөрийн тайлбар. Банк — дансанд орсон хуулгын утга. Гурван текст өөр байж болно.">
          <span>Гүйлгээний утга</span>
        </Tooltip>
      ),
      key: "purpose",
      width: 280,
      render: (_, record) => <PurposeLines record={record} />,
    },
    {
      title: "Нэхэмжлэлийн дүн",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 130,
      render: (value) => moneyFormat(value),
      ...textFilter("amount", "Дүн", asNumber),
    },
    {
      title: "Шимтгэл",
      dataIndex: "feeAmount",
      key: "feeAmount",
      align: "right",
      width: 110,
      render: (value) => moneyCell(value),
    },
    {
      title: "Нийт дүн",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 120,
      render: (value) => moneyCell(value),
    },
    {
      title: "Төлбөрийн суваг",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 120,
      render: (value) => (value ? value.name : "-"),
      ...selectFilter(
        "paymentMethodId",
        paymentMethods
      ),
    },
    {
      title: "Шилжүүлэх данс",
      key: "fromAccountNumber",
      width: 140,
      render: (_, record) =>
        invoiceControlRow(record).fromAccountNumber || "-",
      ...textFilter("fromAccountNumber", "Дансны дугаар"),
    },
    {
      title: "Хүлээн авагч",
      key: "recipient",
      width: 180,
      render: (_, record) => {
        const recipients = record.recipients || [];
        if (!recipients.length) return "-";
        return recipients.map((recipient) => (
          <div key={recipient.account}>
            <div>{recipient.name || recipient.account}</div>
            {recipient.name ? (
              <div className="invoice-recipient-account">{recipient.account}</div>
            ) : null}
          </div>
        ));
      },
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (value) =>
        value === "PAID" ? (
          <Tag color="cyan">Төлөгдсөн</Tag>
        ) : (
          <Tag>Төлөгдөөгүй</Tag>
        ),
    },
    {
      title: "Орлогын дүн",
      dataIndex: "bankIncomeAmount",
      key: "bankIncomeAmount",
      align: "right",
      width: 120,
      render: (value) => moneyCell(value),
    },
    {
      title: "Орлогийн төлөв",
      key: "bankIncomeStatus",
      width: 130,
      align: "center",
      render: (_, record) =>
        record.bankIncomeAmount != null ? (
          <Tag color="green">Орж ирсэн</Tag>
        ) : (
          <Tag>Орж ирээгүй</Tag>
        ),
    },
    {
      title: "Өндөрлөгөө",
      key: "merchantSettleId",
      width: 110,
      render: (_, record) => invoiceControlRow(record).settleIds || "-",
    },
    {
      title: "Өндөрлөгөөний төлөв",
      key: "settleState",
      width: 170,
      align: "center",
      render: (_, record) => {
        const row = invoiceControlRow(record);
        if (row.settleState === "MIXED") {
          return row.settleParts.map((part) => (
            <div key={`${part.id ?? "-"}-${part.state}`}>
              {part.id ?? "-"} {settleStateLabel(part.state)}
            </div>
          ));
        }
        const color =
          row.settleState === "SETTLED"
            ? "cyan"
            : row.settleState === "WAITING"
            ? "blue"
            : row.settleState === "FAILED"
            ? "red"
            : undefined;
        return <Tag color={color}>{settleStateLabel(row.settleState)}</Tag>;
      },
    },
    {
      title: "Өндөрлөгсөн цаг",
      key: "invoiceSettledAt",
      width: 150,
      render: (_, record) => timeCell(invoiceControlRow(record).settledAt),
    },
    {
      title: "Merchant %",
      key: "merchantFeePercent",
      align: "right",
      width: 100,
      render: (_, record) =>
        percentCell(invoiceControlRow(record).merchantFeePercent),
    },
    {
      title: "Мерчантын шимтгэл",
      key: "merchantFee",
      align: "right",
      width: 140,
      render: (_, record) => moneyCell(invoiceControlRow(record).merchantFee),
    },
    {
      title: "XPay %",
      key: "xpayFeePercent",
      align: "right",
      width: 90,
      render: (_, record) =>
        percentCell(invoiceControlRow(record).xpayFeePercent),
    },
    {
      title: "XPay дүн",
      key: "xpayFee",
      align: "right",
      width: 110,
      render: (_, record) => moneyCell(invoiceControlRow(record).xpayFee),
    },
    {
      title: "SPay дүн",
      key: "spayFee",
      align: "right",
      width: 110,
      render: (_, record) => moneyCell(invoiceControlRow(record).spayFee),
    },
    {
      title: "Боловсруулалт %",
      key: "processingFeePercent",
      align: "right",
      width: 120,
      render: (_, record) =>
        percentCell(invoiceControlRow(record).processingFeePercent),
    },
    {
      title: "Боловсруулалтын дүн",
      key: "processingFee",
      align: "right",
      width: 150,
      render: (_, record) =>
        moneyCell(invoiceControlRow(record).processingFee),
    },
    {
      title: "Эцсийн дүн",
      key: "afterFeeAmount",
      align: "right",
      width: 130,
      render: (_, record) => (
        <span style={{ color: "#d46b08", fontWeight: 600 }}>
          {moneyCell(invoiceControlRow(record).afterFeeAmount)}
        </span>
      ),
    },
    {
      title: <span style={{ color: "red", fontSize: 10 }}>DEV BUTTONS!</span>,
      dataIndex: "action",
      key: "action",
      width: 100,
      render: (e, r) => (
        <div>
          <Button className="smallButton" onClick={() => forcePay(r)}>
            PAY
          </Button>
          <Button className="smallButton" onClick={() => forcePayLocal(r)}>
            PAY LOCAL
          </Button>
          <Button
            className="smallButton"
            onClick={() => openInvoice(r)}
            icon={<LinkOutlined />}
          ></Button>
          <Button
            className="smallButton"
            onClick={() => openInvoiceLocal(r)}
            icon={<LinkOutlined style={{ color: "red" }} />}
          ></Button>
        </div>
      ),
    },
  ];


  const invoiceItemColumns = [
    {
      title: "Гүйлгээний утга",
      dataIndex: "description",
      key: "description",
      width: 280,
      render: (value) => {
        const text = purposeText(value);
        if (!text) return "—";
        return (
          <div className="invoice-purpose">
            <Tooltip title={text}>
              <span className="invoice-purpose__text">{text}</span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Нийт дүн",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "НӨАТ",
      dataIndex: "invoiceItemVatInfo",
      key: "invoiceItemVatInfo",
      render: (e) =>
        e?.status == "DONE" ? (
          <Popover title="VAT" content={<p>{JSON.stringify(e.qrJson)}</p>}>
            <Tag color="cyan">Үүссэн</Tag>
          </Popover>
        ) : (
          <Tag color="default">Үүссэгүй</Tag>
        ),
    },
    {
      title: (
        <Tooltip title="Мерчант-с өгсөн хаягыг төлөгдсөний дараа дуудсан статус">
          <span>Мэдээлэлсэн ?</span>
        </Tooltip>
      ),
      dataIndex: "callBackResponse",
      key: "callBackResponse",
      render: (e) =>
        e ? (
          String(e).includes("200") ? (
            <Tag color="cyan">200</Tag>
          ) : (
            <Tag color="default">{e}</Tag>
          )
        ) : (
          <Tag color="default">-</Tag>
        ),
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (e) => (e ? dayjs(e).format("YYYY-MM-DD HH:mm:ss") : "-"),
    },
    {
      title: "Бүт/төрөл",
      dataIndex: "productType",
      key: "productType",
      render: (e) => (e ? e.name : "-"),
    },
    {
      title: "Гүйлгээ",
      dataIndex: "transaction",
      key: "transaction",
      render: (e) => (e ? e.status : "-"),
    },
  ];
  const forcePay = async (record) => {
    var histories = record.qpayHistories;
    if (!histories || histories.length == 0) return;
    var sorted = histories.sort((a, b) => b.id - a.id);
    const qpayUrl = sorted[0].callBackUrl;
    if (!qpayUrl) return;
    axios
      .get(qpayUrl)
      .then((e) => {
        notification.success({
          message: "Success",
          description: "",
        });
      })
      .catch((e) => {
        if (e.response && e.response.data.error)
          notification.error({
            message: "Error",
            description: e.response.data.error.message,
          });
        else
          notification.error({
            message: "Error",
            description: "Failed to pay",
          });
      });
  };
  const forcePayLocal = async (record) => {
    var histories = record.qpayHistories;
    if (!histories || histories.length == 0)
      return notification.error({ message: "no history click link!" });
    var sorted = histories.sort((a, b) => b.id - a.id);
    var qpayUrl = sorted[0].callBackUrl;
    if (!qpayUrl)
      return notification.error({ message: "no history click link!" });
    qpayUrl = String(qpayUrl).replace(
      "https://apispay.esukh.mn",
      "http://localhost:8005"
    );
    axios
      .get(qpayUrl)
      .then((e) => {
        notification.success({
          message: "Success",
          description: "",
        });
      })
      .catch((e) => {
        if (e.response && e.response.data.error)
          notification.error({
            message: "Error",
            description: e.response.data.error.message,
          });
        else
          notification.error({
            message: "Error",
            description: "Failed to pay",
          });
      });
  };
  const openInvoice = async (record) => {
    window.open(`https://smartbilling.mn/invoice?secureId=${record.secureId}`);
  };
  const openInvoiceLocal = async (record) => {
    window.open(`http://localhost:3000/invoice?secureId=${record.secureId}`);
  };
  useEffect(() => {
    refresh();
  }, []);
  const fetchInvoices = async (queryString, page, size) => {
    showLoading();
    const response = await GetAdminInvoices(
      `&page=${page}&size=${size}&${queryString}`
    );
    hideLoading();
    if (response) {
      setData(response.data);
      setPaginationData(response.pagination);
      setSummary(response.summary);
    }
  };
  const changePage = (page) => {
    setPage(page);
  };
  const addInvoice = (value) => {
    setOpenModal(true);
  };
  const closeModal = () => {
    setOpenModal(false);
  };
  const changeSize = (current, size) => {
    setPageSize(size);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [merchantResponse, methods, types] = await Promise.all([
          GetMerchants(),
          GetPaymentMethods(),
          GetProductTypes(),
        ]);
        if (cancelled) return;
        setMerchants(
          (merchantResponse?.data || []).map((row) => ({
            value: row.id,
            label: row.name,
          }))
        );
        setPaymentMethods(
          (methods || []).map((row) => ({
            value: row.id,
            label: row.name,
          }))
        );
        setProductTypes(
          (types || []).map((row) => ({
            value: row.id,
            label: row.name,
          }))
        );
      } catch {
        if (!cancelled) {
          setMerchants([]);
          setPaymentMethods([]);
          setProductTypes([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const refresh = () => {
    setPage(1);
    setSearchFields({ createdAt: todayRange() });
  };
  const toggleDev = () => {
    setDevMode(!devMode);
  };
  const openExcelModal = () => {
    if (searchFields?.createdAt?.[0] && searchFields?.createdAt?.[1]) {
      setExcelRange([
        dayjs(searchFields.createdAt[0]),
        dayjs(searchFields.createdAt[1]),
      ]);
    }
    setExcelPreview(null);
    setExcelModal(true);
  };

  useEffect(() => {
    if (!excelModal || !excelRange?.[0] || !excelRange?.[1]) return;
    let cancelled = false;
    const query = invoiceSearchQueryString(searchFields, {
      createdAtOverride: excelRange,
    });
    if (!query) return;
    setExcelPreviewLoading(true);
    (async () => {
      try {
        const response = await GetAdminInvoices(
          `&${query}&controlSummary=1&page=1&size=1`
        );
        if (cancelled) return;
        setExcelPreview({
          summary: response?.summary || null,
          total: response?.pagination?.total ?? 0,
        });
      } catch {
        if (!cancelled) setExcelPreview(null);
      } finally {
        if (!cancelled) setExcelPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [excelModal, excelRange, searchFields]);
  const handleExcelExport = async () => {
    if (!excelRange?.[0] || !excelRange?.[1]) {
      notification.warning({ message: "Эхлэх/дуусах огноо сонгоно уу" });
      return;
    }
    setExcelExporting(true);
    showLoading();
    try {
      const response = await ExportAdminInvoices(searchFields, {
        createdAtOverride: excelRange,
      });
      if (!response?.success || !response?.data) {
        notification.error({
          message: response?.message || "Excel өгөгдөл авахад алдаа гарлаа",
        });
        return;
      }
      downloadAdminInvoiceExcel(response.data);
      notification.success({
        message: "Excel татагдлаа",
        description: `${response.data.totalCount || 0} мөр`,
      });
      setExcelModal(false);
    } catch (err) {
      notification.error({
        message:
          err.response?.data?.message || err.message || "Excel алдаа гарлаа",
      });
    } finally {
      setExcelExporting(false);
      hideLoading();
    }
  };
  return (
    <div className="vatMerchantListontainer mt-3">
      <Tabs
        activeKey={controlTab}
        onChange={setControlTab}
        items={[
          {
            key: "income",
            label: "Орлогын гүйлгээн",
            children: (
              <>
      {urlTxRangeLabel && (
        <Alert
          type="info"
          showIcon
          closable
          className="mb-2 text-sm"
          onClose={() => setUrlTxRangeLabel(null)}
          message="Reconciliation эсвэл холбоосоор ирсэн гүйлгээний огноо"
          description={
            <>
              «Гүйлгээний огноо» шүүлт: <strong>{urlTxRangeLabel}</strong> (Улаанбаатар, бүтэн
              өдөр). Өөр хүрээ хэрэгтэй бол RangePicker-ээр солино.
            </>
          }
        />
      )}
      <Row style={{ gap: 10 }}>
        <div className="addButtonMerchant">
          <Button
            type="primary"
            className={`${bg_button} ${bg_shadow} rounded-xl`}
            onClick={addInvoice}
          >
            Нэхэмжлэл үүсгэх
          </Button>
        </div>
        <div className="addButtonMerchant">
          <Button
            type="primary"
            className={`${bg_button} ${bg_shadow} rounded-xl`}
            icon={<FileExcelOutlined />}
            onClick={openExcelModal}
          >
            Excel
          </Button>
        </div>
        <div className="addButtonMerchant">
          <Button
            type="primary"
            className={`${bg_button} ${bg_shadow} rounded-xl`}
            onClick={refresh}
          >
            <RedoOutlined />
          </Button>
        </div>
        <div className="addButtonMerchant">
          <Button
            type={"primary"}
            className={`spayDefaultButton ${bg_button} ${bg_shadow} ${
              !devMode && "spayDefaultButtonInactive"
            }`}
            onClick={toggleDev}
          >
            <CodeOutlined />
          </Button>
        </div>
      </Row>
      <Row className="invoice-control-search" align="middle">
        <Input.Search
          size="small"
          allowClear
          placeholder="Нэхэмжлэлийн код"
          style={{ width: 160 }}
          value={invoiceIdDraft}
          onChange={(event) => {
            const value = event.target.value;
            setInvoiceIdDraft(value);
            if (!value) setFilter("id", null);
          }}
          onSearch={(value) => {
            const text = String(value || "").trim();
            if (!text) {
              setFilter("id", null);
              return;
            }
            const invoiceId = asNumber(text);
            if (!invoiceId || Number(invoiceId) <= 0) {
              notification.warning({
                message: "Нэхэмжлэлийн код тоо байна",
              });
              return;
            }
            setFilter("id", invoiceId);
          }}
        />
        <RangePicker
          size="small"
          value={searchFields.createdAt || null}
          onChange={(dates) =>
            setFilter(
              "createdAt",
              dates && dates[0] && dates[1] ? dates : null
            )
          }
          allowClear
          placeholder={["Гүйлгээний огноо", "Дуусах"]}
        />
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Бизнесийн нэр"
          style={{ width: 220 }}
          value={searchFields.merchantId || undefined}
          onChange={(value) => setFilter("merchantId", value)}
          options={merchants}
        />
        <Select
          size="small"
          allowClear
          placeholder="Төлөв"
          style={{ width: 150 }}
          value={searchFields.status || undefined}
          onChange={(value) => setFilter("status", value)}
          options={[
            { value: "PAID", label: "Төлөгдсөн" },
            { value: "NOT_PAID", label: "Төлөгдөөгүй" },
          ]}
        />
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Бүтээгдэхүүн"
          style={{ width: 180 }}
          value={searchFields.productTypeId || undefined}
          onChange={(value) => setFilter("productTypeId", value)}
          options={productTypes}
        />
        <Input.Search
          size="small"
          allowClear
          placeholder="Хүлээн авагч"
          style={{ width: 220 }}
          value={recipientDraft}
          onChange={(event) => {
            const value = event.target.value;
            setRecipientDraft(value);
            if (!value) setFilter("recipient", null);
          }}
          onSearch={(value) => setFilter("recipient", String(value || "").trim())}
        />
        <Input.Search
          size="small"
          allowClear
          placeholder="Гүйлгээний утга"
          style={{ width: 240 }}
          value={purposeDraft}
          title="Нэхэмжлэл, мөр, банкны утгаас хайна. 3-аас доошгүй тэмдэгт, огноо 31 хоногоос хэтрэхгүй."
          onChange={(event) => {
            const value = event.target.value;
            setPurposeDraft(value);
            if (!value) setFilter("purpose", null);
          }}
          onSearch={(value) => {
            const text = String(value || "").trim();
            if (!text) {
              setFilter("purpose", null);
              return;
            }
            if (text.length < 3) {
              notification.warning({
                message: "Гүйлгээний утга хамгийн багадаа 3 тэмдэгт",
              });
              return;
            }
            const range = searchFields.createdAt;
            if (!range?.[0] || !range?.[1]) {
              notification.warning({
                message: "Гүйлгээний утга хайхад огноо сонгоно уу",
              });
              return;
            }
            const days =
              dayjs(range[1]).startOf("day").diff(dayjs(range[0]).startOf("day"), "day") +
              1;
            if (days > 31) {
              notification.warning({
                message: "Гүйлгээний утга хайлт хамгийн ихдээ 31 хоног",
              });
              return;
            }
            setFilter("purpose", text);
          }}
        />
      </Row>
      <div className="tableContainer">
        <Table
          className="invoice-control-table"
          size="small"
          columns={defaultColumns.filter((e) => {
            if (e.key == "action") return devMode;
            return true;
          })}
          rowKey={(record) => record.id}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                size="small"
                className="invoice-control-table"
                columns={invoiceItemColumns}
                dataSource={record.invoiceItems}
                pagination={false}
              />
            ),
            expandIconColumnIndex: 0,
            rowExpandable: () => {
              return true;
            },
          }}
          scroll={{
            x: "max-content",
            y: "calc(100vh - 38vh)",
          }}
          pagination={false}
          dataSource={data}
          summary={() => {
            const summaryMoney = (value) =>
              value == null || value === "" ? "-" : moneyFormat(value);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="summaryRow">
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1}>Нийт</Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} />
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} />
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} />
                  <Table.Summary.Cell index={8} align="right">
                    {summaryMoney(summary?.totalAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    {summaryMoney(summary?.feeAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} align="right">
                    {summaryMoney(summary?.invoiceTotalAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11} />
                  <Table.Summary.Cell index={12} />
                  <Table.Summary.Cell index={13} />
                  <Table.Summary.Cell index={14} />
                  <Table.Summary.Cell index={15} align="right">
                    {summaryMoney(summary?.bankIncomeAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={16} />
                  <Table.Summary.Cell index={17} />
                  <Table.Summary.Cell index={18} />
                  <Table.Summary.Cell index={19} />
                  <Table.Summary.Cell index={20} align="right">
                    -
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={21} align="right">
                    {summaryMoney(summary?.merchantFee)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={22} align="right">
                    -
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={23} align="right">
                    {summaryMoney(summary?.xpayFee)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={24} align="right">
                    {summaryMoney(summary?.spayFee)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={25} align="right">
                    -
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={26} align="right">
                    {summaryMoney(summary?.processingFee)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={27} align="right">
                    <span className="invoice-control-final">
                      {summaryMoney(summary?.afterFeeAmount)}
                    </span>
                  </Table.Summary.Cell>
                  {devMode ? <Table.Summary.Cell index={28} /> : null}
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        ></Table>
        <TablePagination
          paginationData={paginationData}
          page={page}
          changePage={changePage}
          changeSize={changeSize}
        />
      </div>
              </>
            ),
          },
          {
            key: "expense",
            label: "Зарлагын гүйлгээнүүд",
            children: (
              <ExpenseTransactions
                merchants={merchants}
                active={controlTab === "expense"}
              />
            ),
          },
          {
            key: "invoiceEvents",
            label: "Пос нэхэмжлэл",
            children: (
              <InvoiceEventLogTab
                merchants={merchants}
                active={controlTab === "invoiceEvents"}
              />
            ),
          },
        ]}
      />
      {openModal && (
        <AdminInvoiceAdd
          open={openModal}
          close={closeModal}
          refresh={refresh}
        />
      )}
      <Modal
        title="Гүйлгээний хяналт — Excel"
        open={excelModal}
        onCancel={() => !excelExporting && setExcelModal(false)}
        okText="Татах"
        cancelText="Болих"
        confirmLoading={excelExporting}
        onOk={handleExcelExport}
        destroyOnClose
        width={640}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Шүүлт"
            description={
              <ul className="mb-0 pl-4 text-sm">
                {describeInvoiceExportFilters(
                  searchFields,
                  { merchants, paymentMethods, productTypes },
                  excelRange
                ).map((line) => (
                  <li
                    key={line.key || line.label}
                    className={line.warn ? "text-amber-700" : ""}
                  >
                    <Text type="secondary">{line.label}:</Text> {line.value}
                  </li>
                ))}
              </ul>
            }
          />
          <div>
            <div className="mb-1 text-sm font-medium">Excel огнооны цонх</div>
            <RangePicker
              style={{ width: "100%" }}
              value={excelRange}
              onChange={(v) => setExcelRange(v)}
              allowClear={false}
            />
            <Text type="secondary" className="mt-1 block text-xs">
              Огноо Asia/Ulaanbaatar өдөрөөр API руу явна (хүснэгттэй ижил
              дүрэм).
            </Text>
          </div>
          <InvoiceSummaryPreview
            title="Excel файлын хөл дүн (API нийлбэр — хүснэгтийн доод мөртэй ижил)"
            summary={excelPreview?.summary}
            total={excelPreview?.total}
            loading={excelPreviewLoading}
          />
          {summary && (
            <InvoiceSummaryPreview
              title="Одоогийн хүснэгтийн доод мөр (хүснэгтийн огнооны шүүлт)"
              summary={summary}
              total={paginationData?.total}
              loading={false}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default AdminInvoice;
